import { useField, useFormikContext } from 'formik';
import { useRef, FocusEvent, useEffect, useState, ChangeEvent } from 'react';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { useLocale } from '../../../../context/Localization/localization';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { PhonePatternMask } from '../PhonePatternMask/PhonePatternMask';
import { KioskFormPhoneFieldProps, NumberMap } from '../FormPhoneField.types';
import { PhoneFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormPhoneField = ({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required,
    autoFocus,
    keyboardLayout,
    shiftAfterSpace,
    shiftAfterPeriod,
    shiftAfterAmpersand,
    hideKeyboardOnClickOut,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
    initiallyMasked,
    onChangeCallback,
    tabFocusRefContext,
    noValidation,
}: KioskFormPhoneFieldProps) => {
    const [field, meta] = useField({
        name: name,
        validate: noValidation ? undefined : PhoneFieldValidation({
            required: required ?? false,
            label,
            name,
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * The input value ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal value state.
     */
    const valueRef = useRef<string>(field.value ?? '');
    /**
     * The caret position ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal caret position state.
     */
    const caretPositionRef = useRef<number | null>(null);
    /**
     * The focus ref to prevent a stale state when the on-screen keyboard
     * calls any functions that need to know if the field is focused or not.
     */
    const isFocused = useRef<boolean>(false);
    /**
     * The on-screen keyboard input ref is used to prevent a stale state when we call to update it
     * from keyboard context functions. It is used to prevent the onChange function that recieves a
     * callback from the pattern mask from resetting the caret position incorrectly after we set it
     * using the keyboard context setValue function.
     */
    const wasOSKInput = useRef<boolean>(true);
    /**
     * Boolean state to update the email pattern mask once the field has been focused
     * so that it never remasks the value.
     */
    const [wasUnmasked, setWasUnmasked] = useState<boolean>(false);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const {
        dealer: { primaryLanguage },
    } = useKioskState();
    const locale = useLocale();

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    useEffect(() => {
        /**
         * Updates the internal caret position when the internal MUI value finishes changing from the
         * pattern mask.
         */
        if (inputRef.current) {
            inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);

            /**
             * This should run after the onChange function is triggered by the pattern mask onAccept
             * which prevents the caret position from updating the the internal pattern mask position
             * incorrectly.
             */
            if (wasOSKInput.current) {
                wasOSKInput.current = false;
            }
        }
    }, [inputRef.current?.value]);

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const getValue = (): GetValueReturn => {
        /**
         * We initialize the wasOSKInput ref here to be true so that onChange doesn't
         * set a new caret position when it runs.
         */
        wasOSKInput.current = true;
        /**
         * We maintain an internal state of the phone number with no formatting, so
         * we have a caret position map for where the on-screen keyboard should start based
         * on the caret position that MUI gives us with the formatting.
         */
        const convertMaskedToUnmaskedCaretPosition: NumberMap = {
            0: 0,
            1: 0,
            2: 1,
            3: 2,
            4: 3,
            5: 3,
            6: 3,
            7: 4,
            8: 5,
            9: 6,
            10: 6,
            11: 7,
            12: 8,
            13: 9,
            14: 10,
        };

        if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
            const selectionStart: number = inputRef.current.selectionStart;
            const selectionEnd: number = inputRef.current.selectionEnd;

            // TODO: Handle selection ranges

            const caretPositionStart: number = convertMaskedToUnmaskedCaretPosition[selectionStart];
            const caretPositionEnd: number = convertMaskedToUnmaskedCaretPosition[selectionEnd];

            return {
                inputValue: valueRef.current,
                caretPositionStart: caretPositionStart,
                caretPositionEnd: caretPositionEnd,
            };
        }
        else {
            return {
                inputValue: valueRef.current,
                caretPositionStart: valueRef.current.length,
                caretPositionEnd: valueRef.current.length,
            };
        }
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const setValue = ({ value, caretPosition }: SetValueProps): void => {
        /**
         * When the on-screen keyboard passes the caret position back to us,
         * we need to convert that position to the correct position with formatting.
         * This map handles the conversion back to the caret position with formatting.
         */
        const convertUnmaskedToMaskedCaretPosition: NumberMap = {
            0: 1,
            1: 2,
            2: 3,
            3: 4,
            4: 7,
            5: 8,
            6: 9,
            7: 11,
            8: 12,
            9: 13,
            10: 14,
        };

        caretPositionRef.current = convertUnmaskedToMaskedCaretPosition[caretPosition];
        onChangeCallback && onChangeCallback();
        valueRef.current = value;
        setFieldValue(name, value);
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
        /**
         * When the length of the phone number is 11, we want to cut out the new character that was added
         * and restore the string and caret position back to what it was prior to the input. This
         * if statement handles preventing the on-screen keyboard from inserting new characters since
         * it does not understand string limitations defined in the mask or on MUI.
         */
        if (value.length === 11) {
            const newValue: string = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
            const newCaretPosition: number = caretPosition - 1;

            return {
                value: newValue,
                caretPosition: newCaretPosition,
            };
        }

        return {
            value: value,
            caretPosition: caretPosition,
        };
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const onKeyboardInput = (): void => {
        if (inputRef.current && !isFocused.current) {
            inputRef.current.focus();
        }
    };

    const { onFocus, onBlur } = useKeyboardIntegration({
        fieldValue: field.value,
        getValue: getValue,
        setValue: setValue,
        onInputChange: onInputChange,
        onKeyboardInput: onKeyboardInput,
        keyboardLayout: keyboardLayout,
        shiftAfterPeriod: shiftAfterPeriod,
        shiftAfterSpace: shiftAfterSpace,
        shiftAfterAmpersand: shiftAfterAmpersand,
        hideKeyboardOnClickOut: hideKeyboardOnClickOut,
        capsLock: capsLock,
    });

    const handleOnBlur = () => {
        onBlur();
        isFocused.current = false;
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        /**
         * We don't want the onAccept from the mask to update anything when
         * the on-screen keyboard is used, so if we use the on-screen keyboard
         * we skip this section of code.
         */
        if (!wasOSKInput.current) {
            caretPositionRef.current = event.target.selectionEnd;
            onChangeCallback && onChangeCallback();
            valueRef.current = event.target.value;
            setFieldValue(name, event.target.value);
        }
    };

    const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        /**
         * When the field is initially masked and has not already been unmasked,
         * we clear the field and formik value.
         */
        if (initiallyMasked && !isFocused.current && !wasUnmasked) {
            valueRef.current = '';
            setFieldValue(name, '');
        }
        if (!wasUnmasked) {
            // Handles preventing the field from remasking after the field has been focused once.
            setWasUnmasked(true);
        }
        isFocused.current = true;
        onFocus(event);
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'tel'}
            type={'tel'}
            inputRef={inputRef}
            value={valueRef.current}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            capsLock={capsLock}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            InputProps={{
                inputComponent: PhonePatternMask as any,
                inputProps: {
                    isMasked: initiallyMasked && !isFocused.current && !wasUnmasked,
                },
            }}
            onKeyDown={() => {
                if (wasOSKInput.current) {
                    wasOSKInput.current = false;
                }
            }}
        />
    );
};
